import _ from 'underscore'
import React, { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Badge from '@mui/material/Badge'

import Tooltip from 'components/Tooltip'
import KeyboardAccessible from 'components/KeyboardAccessible'

import UnreadSiteMessagesCount from 'components/SideNavbar/UnreadSiteMessagesCount'
import findActiveNavbarLink, { LINKS_BY_ROLE } from 'components/SideNavbar/findActiveNavbarLink'

import { useSessionContext } from 'hooks/SessionContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import Apptentive from 'helpers/apptentive'
import Analytics from 'helpers/analytics'

import { color } from 'styles/colors'

const ICON_FONT_SIZE = 'large'

type Props = {
  displayMiniVariantSidebar: boolean
  itemSelectedCallback: (() => void) | null
}

const NavbarItems = ({ displayMiniVariantSidebar, itemSelectedCallback }: Props) => {
  const { session: { currentRole = 'unknown', isDeveloper } } = useSessionContext()
  const { hasAccess } = useFeaturesContext()
  const navigate = useNavigate()
  const location = useLocation()

  let linksForRole = LINKS_BY_ROLE[currentRole]

  if (!isDeveloper) {
    linksForRole = LINKS_BY_ROLE[currentRole].filter(link => link.text !== 'Device Aggregator')
  }

  if (!hasAccess('tech_assistant')) {
    linksForRole = linksForRole.filter(link => link.text !== 'Tech Assistant')
  }

  const onClickLink = useCallback((text: string, href: string, apptentiveEvent: string) => {
    if (itemSelectedCallback) {
      itemSelectedCallback()
    }

    if (href === location.pathname) { return }

    navigate(href)

    Analytics.track({ category: 'Main Navigation', action: `Click ${text}` })

    if (apptentiveEvent) {
      Apptentive.sendEvent(apptentiveEvent)
    }
  }, [navigate, location.pathname, itemSelectedCallback])

  const activeLink = findActiveNavbarLink(currentRole, location.pathname)

  return (
    <S.Navbar displayMiniVariantSidebar={displayMiniVariantSidebar}>
      {_.map(linksForRole, (link) => {
        const isActive = link.text === activeLink?.text
        const siteMessagesCountComponent = link.text === 'Updates' ? <UnreadSiteMessagesCount /> : null
        const Icon = link.icon

        return (
          <S.NavLink
            isActive={isActive}
            onClick={() => onClickLink(link.text, link.href, link.appTentiveId)}
            displayMiniVariantSidebar={displayMiniVariantSidebar}
            key={link.text}
          >
            <Badge invisible={!siteMessagesCountComponent || isActive} badgeContent={siteMessagesCountComponent} >
              <Tooltip title={link.text} placement="right">
                <Icon fontSize={ICON_FONT_SIZE} aria-label={link.text + ' Link Icon'} />
              </Tooltip>
            </Badge>

            {!displayMiniVariantSidebar && <S.LinkText>{link.text}</S.LinkText>}
          </S.NavLink>
        )
      })}
    </S.Navbar>
  )
}

export default NavbarItems

const S = {
  Navbar: styled.div.withConfig({
    shouldForwardProp: (prop) => !['displayMiniVariantSidebar'].includes(prop)
  })<{ displayMiniVariantSidebar: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ displayMiniVariantSidebar }) => displayMiniVariantSidebar && 'justify-content: center'};
  `,
  NavLink: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['isActive', 'displayMiniVariantSidebar'].includes(prop)
  })<{ isActive: boolean, displayMiniVariantSidebar: boolean }>`
    outline-offset: -1px;
    display: flex;
    align-items: center;
    ${({ displayMiniVariantSidebar }) => displayMiniVariantSidebar && 'justify-content: center'};
    font-weight: 600;
    cursor: pointer;
    background-color: ${({ isActive }) => isActive ? color('navbarBackgroundActive') : color('navbarBackground')};
    color: ${({ isActive }) => color(isActive ? 'navLinkText' : 'navLinkTextNotSelected')};
    padding: 10px 20px;
    border: 1px solid transparent;

    &:hover {
      background-color: ${({ isActive }) => isActive ? color('navbarBackgroundActive') : color('navbarBackgroundHover')};
      border: ${({ isActive }) => isActive ? 'transparent' : color('navbarBackgroundHoverBorder')} solid 1px ;

    }
    &:hover > h3{
      color: ${({ isActive }) => isActive ? color('navLinkText') : color('navbarHoverText')};
    }
  `,
  LinkText: styled.h3`
    font-size: 13pt;
    color: inherit;
    font-family: inter.bold;
    margin-left: 10px;
    margin-bottom: 0px;
  `
}
